import axios from 'axios'
import { getSession, signOut } from 'next-auth/react'
import type { ReportQueryParams, ReportNavResponse, ReportPastResponse, ReportScheduledResponse, ReportDetailResponse, ReportPreset, ChartGroupBy, ScheduledReportDTO, ScheduleDetailInput, TriggerReportExecutionResponse, CreateScheduledReportInput, CreateScheduledReportResponse, PreviewScheduledReportResponse } from '@/types/reports'
import type { AccountMetricView } from '@/types/metrics'
import type { Competitor, CompetitorProfile, CompetitorPlatformOption, CreateCompetitorInput, UpdateCompetitorInput, ProfileInput, UpdateProfileInput } from '@/types/competitor'
import type { Guardrail, CreateGuardrailInput, UpdateGuardrailInput, UpsertGuardrailOverrideInput, CapabilityTypeOption } from '@/types/guardrail'
import type { Department } from '@/types/department'
import type { AuditLogPage, AuditLogRequestParams } from '@/types/auditLog'
import type { NewsletterExecutionDetailResponse, NewsletterExecutionsListResponse } from '@/types/newsletterReports'
import type {
  PlanTemplate,
  PlanTemplateWithLines,
  PlanTemplateCatalogItem,
  CreatePlanTemplateInput,
  UpdatePlanTemplateInput,
  ClonePlanTemplateInput,
  PlanTemplateLineInput,
  PlanTemplateAgent,
  AccountPlan,
  AccountPlanWithLines,
  CreateAccountPlanInput,
  CloneAccountPlanInput,
  CloneAccountPlanResult,
  UpdateAccountPlanInput,
  AccountPlanLineInput,
  CloneAccountPlanLineInput,
  AccountPlanAgent,
  ActivatePlanInput,
  ActivatePlanResult,
  PlanActivationReadiness,
  PlanCostEstimate,
  PlanCalendarResponse,
  PauseScope,
} from '@/types/plans'

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api'

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor to add auth token from NextAuth session
api.interceptors.request.use(async (config) => {
  const session = await getSession()
  if (session?.accessToken) {
    config.headers.Authorization = `Bearer ${session.accessToken}`
  }
  return config
})

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/update-password')) {
      // Get current session
      const session = await getSession()
      
      if (session?.refreshToken) {
        try {
          // Try to refresh token using our backend API
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken: session.refreshToken
          })
          
          const { accessToken, refreshToken: newRefreshToken } = response.data
          
          // Update the session (this would require custom session update logic)
          // For now, we'll sign out and require re-login for security
          await signOut({ callbackUrl: '/login' })
          
          return Promise.reject(error)
        } catch (refreshError) {
          // Refresh failed, sign out
          await signOut({ callbackUrl: '/login' })
        }
      } else {
        // No refresh token, sign out
        await signOut({ callbackUrl: '/login' })
      }
    }
    return Promise.reject(error)
  }
)

// API functions
export const authAPI = {
  register: (data: {
    email: string
    password: string
    firstName: string
    lastName: string
    organizationName: string
  }) => api.post('/auth/register', data),
  
  // These are now handled by NextAuth
  login: (data: { email: string; password: string }) => 
    api.post('/auth/login', data),
  
  logout: async (refreshToken?: string) => {
    if (refreshToken) {
      return api.post('/auth/logout', { refreshToken })
    }
    return Promise.resolve()
  }
}

export const agentsAPI = {
  getAll: () => api.get('/agents'),

  create: (data: {
    name: string
    description?: string
    type: string
    accountId: string
    frequency: string
    scheduleDayOfWeek?: string
    scheduleDayOfMonth?: string
    scheduleTime?: string
    configuration?: any
  }) => api.post('/agents', data),

  getById: (id: string) => api.get(`/agents/${id}`),

  update: (id: string, data: any) => api.put(`/agents/${id}`, data),

  delete: (id: string) => api.delete(`/agents/${id}`),

  execute: (id: string, inputData: any) =>
    api.post(`/agents/${id}/execute`, { inputData }),

  getExecutions: (id: string, params?: { status?: string; limit?: number; offset?: number; sortBy?: string; sortDir?: string }) =>
    api.get(`/agents/${id}/executions`, { params }),

  updateStatus: (id: string, action: 'activate' | 'deactivate' | 'configure') =>
    api.patch(`/agents/${id}/status`, { action }),

  scheduleExecutions: (
    id: string,
    data:
      | { mode: 'count'; count?: number }
      | { mode: 'period'; startDate: string; endDate: string }
  ) => api.post(`/agents/${id}/executions`, {
    ...data,
    clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }),
}

export const orgAgentsAPI = (orgId: string) => ({
  getAll: (params?: Record<string, string | number>) =>
    api.get(orgId ? `/org/${orgId}/agents` : '/agents', { params }),

  create: (data: {
    name: string
    description?: string
    type: string
    agentTypeId?: string
    accountId: string
    frequency: string
    scheduleDayOfWeek?: string
    scheduleDayOfMonth?: string
    scheduleTime?: string
    configuration?: any
  }) => api.post(`/org/${orgId}/agents`, data),

  getById: (id: string) => api.get(`/org/${orgId}/agents/${id}`),

  update: (id: string, data: any) => api.put(`/org/${orgId}/agents/${id}`, data),

  delete: (id: string) => api.delete(`/org/${orgId}/agents/${id}`),

  updateStatus: (id: string, action: 'activate' | 'deactivate' | 'configure') =>
    api.patch(`/org/${orgId}/agents/${id}/status`, { action }),
})

// VP-1374: plan templates (agency-authored blueprints — global or org-owned, D4).
export const planTemplatesAPI = {
  // VP-1436 (Phase A): list items are enriched with `templateAgents` (resolved
  // agent-type name + schedule label per line). Superset of PlanTemplate —
  // existing table callers simply ignore the extra field.
  getAll: () => api.get<PlanTemplateCatalogItem[]>('/plan-templates'),

  getById: (id: string) => api.get<PlanTemplateWithLines>(`/plan-templates/${id}`),

  create: (data: CreatePlanTemplateInput) => api.post<PlanTemplate>('/plan-templates', data),

  update: (id: string, data: UpdatePlanTemplateInput) => api.put<PlanTemplate>(`/plan-templates/${id}`, data),

  delete: (id: string) => api.delete<void>(`/plan-templates/${id}`),

  // VP-1405: duplicate a template (and its lines) as a new draft. makeGlobal
  // is only honoured for portal admins.
  clone: (id: string, data: ClonePlanTemplateInput) =>
    api.post<PlanTemplateWithLines>(`/plan-templates/${id}/clone`, data),

  createLine: (templateId: string, data: PlanTemplateLineInput) =>
    api.post<PlanTemplateAgent>(`/plan-templates/${templateId}/lines`, data),

  updateLine: (templateId: string, lineId: string, data: PlanTemplateLineInput) =>
    api.put<PlanTemplateAgent>(`/plan-templates/${templateId}/lines/${lineId}`, data),

  deleteLine: (templateId: string, lineId: string) =>
    api.delete<void>(`/plan-templates/${templateId}/lines/${lineId}`),

  // VP-1417: read-only monthly schedule preview, computed from each line's slots.
  getCalendar: (id: string, month: string) =>
    api.get<PlanCalendarResponse>(`/plan-templates/${id}/calendar`, { params: { month } }),
}

// VP-1465/VP-1484: overloaded so a call with a non-empty `targetAccountIds`
// resolves to the fan-out CloneAccountPlanResult response, while every other
// call (including every pre-existing ClonePlanModal.tsx call site, which never
// sends targetAccountIds) keeps resolving to the single-plan response it
// already destructures `res.data.id` from.
interface CloneAccountPlanFn {
  (planId: string, data: CloneAccountPlanInput & { targetAccountIds: string[] }): ReturnType<typeof api.post<CloneAccountPlanResult>>
  (planId: string, data: CloneAccountPlanInput): ReturnType<typeof api.post<AccountPlanWithLines>>
}

// VP-1434: resolved metrics of one account (VP-1945), the goal metric picker's options.
export const accountMetricsAPI = (accountId: string) => ({
  list: () => api.get<{ metrics: AccountMetricView[] }>(`/accounts/${accountId}/metrics`),
})

// VP-1972: account guardrails and VirtualCrew defaults with account overrides.
export const accountGuardrailsAPI = (accountId: string) => ({
  list: (params?: { status?: 'active' | 'all' }) =>
    api.get<{ guardrails: Guardrail[] }>(`/accounts/${accountId}/guardrails`, { params }),

  get: (id: string) => api.get<{ guardrail: Guardrail }>(`/accounts/${accountId}/guardrails/${id}`),

  create: (data: CreateGuardrailInput) =>
    api.post<{ guardrail: Guardrail }>(`/accounts/${accountId}/guardrails`, data),

  update: (id: string, data: UpdateGuardrailInput) =>
    api.patch<{ guardrail: Guardrail }>(`/accounts/${accountId}/guardrails/${id}`, data),

  remove: (id: string) => api.delete<void>(`/accounts/${accountId}/guardrails/${id}`),

  upsertOverride: (id: string, data: UpsertGuardrailOverrideInput) =>
    api.put<{ guardrail: Guardrail }>(`/accounts/${accountId}/guardrails/${id}/override`, data),

  removeOverride: (id: string) => api.delete<void>(`/accounts/${accountId}/guardrails/${id}/override`),
})

export const accountCompetitorsAPI = (accountId: string) => ({
  list: (params?: { status?: 'active' | 'all' }) =>
    api.get<{ competitors: Competitor[]; platforms: CompetitorPlatformOption[] }>(
      `/accounts/${accountId}/competitors`,
      { params }
    ),

  get: (id: string) => api.get<{ competitor: Competitor }>(`/accounts/${accountId}/competitors/${id}`),

  create: (data: CreateCompetitorInput) =>
    api.post<{ competitor: Competitor }>(`/accounts/${accountId}/competitors`, data),

  update: (id: string, data: UpdateCompetitorInput) =>
    api.patch<{ competitor: Competitor }>(`/accounts/${accountId}/competitors/${id}`, data),

  remove: (id: string) => api.delete<void>(`/accounts/${accountId}/competitors/${id}`),

  createProfile: (id: string, data: ProfileInput) =>
    api.post<{ profile: CompetitorProfile }>(`/accounts/${accountId}/competitors/${id}/profiles`, data),

  updateProfile: (id: string, profileId: string, data: UpdateProfileInput) =>
    api.patch<{ profile: CompetitorProfile }>(
      `/accounts/${accountId}/competitors/${id}/profiles/${profileId}`,
      data
    ),

  removeProfile: (id: string, profileId: string) =>
    api.delete<void>(`/accounts/${accountId}/competitors/${id}/profiles/${profileId}`),
})

// VP-1374: account plans (a template instantiated onto one account, or ad-hoc).
// Mounted under /accounts/:accountId/plans on the backend — mirrors the
// accountStaffAPI(accountId)/accountCatalogAPI factory-function convention.
export const accountPlansAPI = (accountId: string) => ({
  getAll: () => api.get<AccountPlan[]>(`/accounts/${accountId}/plans`),

  // VP-1595: every plan with its lines + bound agent, for the calendar Chains view.
  getAllWithAgents: () =>
    api.get<AccountPlanWithLines[]>(`/accounts/${accountId}/plans`, { params: { include: 'planAgents' } }),

  getById: (planId: string) => api.get<AccountPlanWithLines>(`/accounts/${accountId}/plans/${planId}`),

  create: (data: CreateAccountPlanInput) =>
    api.post<AccountPlanWithLines>(`/accounts/${accountId}/plans`, data),

  // VP-1405: duplicate a plan, its bound agents, and schedule slots onto a
  // target account (defaults to this account) as a new draft.
  clone: ((planId: string, data: CloneAccountPlanInput) =>
    api.post<AccountPlanWithLines | CloneAccountPlanResult>(`/accounts/${accountId}/plans/${planId}/clone`, data)
  ) as CloneAccountPlanFn,

  update: (planId: string, data: UpdateAccountPlanInput) =>
    api.put<AccountPlan>(`/accounts/${accountId}/plans/${planId}`, data),

  createLine: (planId: string, data: AccountPlanLineInput) =>
    api.post<AccountPlanAgent>(`/accounts/${accountId}/plans/${planId}/lines`, data),

  // VP-1457: Step 2 "Clone Existing Agent" tab — clones baseAgentId into a
  // fresh, plan-bound AiAgent (cloneAgentForPlan) and appends it as a new
  // line, optionally applying trigger/dependsOn/slots in the same request.
  cloneLine: (planId: string, data: CloneAccountPlanLineInput) =>
    api.post<AccountPlanAgent>(`/accounts/${accountId}/plans/${planId}/lines/clone`, data),

  updateLine: (planId: string, lineId: string, data: AccountPlanLineInput) =>
    api.put<AccountPlanAgent>(`/accounts/${accountId}/plans/${planId}/lines/${lineId}`, data),

  deleteLine: (planId: string, lineId: string) =>
    api.delete<void>(`/accounts/${accountId}/plans/${planId}/lines/${lineId}`),

  activate: (planId: string, data: ActivatePlanInput) =>
    api.post<ActivatePlanResult>(`/accounts/${accountId}/plans/${planId}/activate`, data),

  // VP-1409: read-only readiness pre-check. Called on the "Activate Plan"
  // click so a not-ready plan renders the failure banner immediately instead
  // of only after the two activation confirmation modals.
  checkActivationReadiness: (planId: string) =>
    api.get<{ data: PlanActivationReadiness }>(`/accounts/${accountId}/plans/${planId}/activation-readiness`),

  // VP-1379: draft cost preview — agency-only (backend gates this on
  // plan:manage, not plan:view), read-only. overridePeriod lets a still-draft
  // plan (whose stored period is null until activation) be previewed against
  // the same candidate dates about to be submitted to activate() — omit it
  // to fall back to the plan's own stored period.
  getCostEstimate: (planId: string, overridePeriod?: { periodStart: string; periodEnd: string }) =>
    api.get<{ data: PlanCostEstimate }>(`/accounts/${accountId}/plans/${planId}/cost-estimate`, {
      params: overridePeriod,
    }),

  // VP-1401: pauseScope controls which non-terminal AgentExecution rows also
  // get paused — 'unstarted_only' (default) leaves already-started
  // executions running, 'all' pauses those too.
  pause: (planId: string, pauseScope: PauseScope = 'unstarted_only') =>
    api.post<AccountPlan>(`/accounts/${accountId}/plans/${planId}/pause`, { pauseScope }),

  resume: (planId: string) => api.post<AccountPlan>(`/accounts/${accountId}/plans/${planId}/resume`),

  // VP-1668: archive an ended or draft plan (hides from working views).
  archive: (planId: string) => api.post<AccountPlan>(`/accounts/${accountId}/plans/${planId}/archive`),

  // VP-1417: fully resolved daily execution slots for one month. Draft plans are
  // computed from the bound agents' schedule; active/paused plans read their
  // materialized executions.
  getCalendar: (planId: string, month: string) =>
    api.get<PlanCalendarResponse>(`/accounts/${accountId}/plans/${planId}/calendar`, { params: { month } }),
})

export const billingAPI = {
  getUsage: () => api.get('/billing/usage'),
  getPlans: () => api.get('/billing/plans'),
  subscribe: (planId: string) => api.post('/billing/subscribe', { planId }),
  cancelSubscription: () => api.post('/billing/cancel'),
  getInvoices: () => api.get('/billing/invoices')
}

export const orgBillingAPI = (orgId: string) => ({
  getInvoices: (params?: {
    page?: number
    limit?: number
    startDate?: string
    endDate?: string
    accountId?: string
    subscriptionId?: string
    status?: string
  }) => api.get(`/org/${orgId}/billing/invoices`, { params }),
  getInvoiceById: (id: string) => api.get(`/org/${orgId}/billing/invoices/${id}`),
  createInvoice: (data: unknown) => api.post(`/org/${orgId}/billing/invoices`, data),
})

export const orgProductsAPI = (orgId: string) => ({
  getAll: () => api.get(`/org/${orgId}/products`),
})

export const auditLogsAPI = {
  list: (path: string, params: AuditLogRequestParams, signal?: AbortSignal) =>
    api.get<AuditLogPage>(path, { params, signal }),
}

export const executionsAPI = {
  getAll: (params?: any) => api.get('/executions', { params }),
  
  getById: (id: string) => api.get(`/executions/${id}`),

  getLink: (id: string) => api.get(`/executions/${id}/link`),

  // VP-1916: Content Creator human steps 1 (Input) and 3 (Review Outline) go
  // through the step engine; the page then follows the step states.
  submitContentStep: (executionId: string, stepNo: 1 | 3 | 5 | 6, body: Record<string, unknown>) =>
    api.post(`/executions/${executionId}/content/steps/${stepNo}/submit`, body),

  getContentState: (executionId: string) => api.get(`/executions/${executionId}/content/state`),

  // VP-1924: Review Outline back to Input (3), Approve Blog back to Review Blog (6).
  sendBackContentStep: (executionId: string, stepNo: 3 | 6, reason: string) =>
    api.post(`/executions/${executionId}/content/steps/${stepNo}/send-back`, { reason }),

  // VP-1920: Generate Image on the Review Blog step (runs synchronously in the portal).
  generateContentImage: (executionId: string, body: { keywords: string[] | string; blogTitle?: string }) =>
    api.post(`/executions/${executionId}/content/generate-image`, body),

  resolveNeedsAttention: (executionId: string, action: 'retry' | 'skip' | 'cancel', note?: string) =>
    api.post(`/executions/${executionId}/resolve`, { action, ...(note ? { note } : {}) }),
  
  create: (data: any) => api.post('/executions', data),
  
  update: (id: string, data: any) => api.put(`/executions/${id}`, data),
  
  delete: (id: string) => api.delete(`/executions/${id}`),
  
  getAgentsWithCounts: () => api.get('/executions/agents-with-counts'),
  
  getStats: (params?: any) => api.get('/executions/stats', { params }),
  
  // Execution Steps
  createStep: (executionId: string, data: any) => 
    api.post(`/executions/${executionId}/steps`, data),
  
  getSteps: (executionId: string) => 
    api.get(`/executions/${executionId}/steps`),
  
  getStepById: (id: string) => api.get(`/executions/steps/${id}`),

  updateStep: (id: string, data: any) => api.put(`/executions/steps/${id}`, data),

  deleteStep: (id: string) => api.delete(`/executions/steps/${id}`),

  getPendingApprovals: () => api.get('/executions/pending-approvals'),

  approveStep: (stepId: string, data: { outputData?: any; approverNote?: string }) =>
    api.post(`/executions/steps/${stepId}/approve`, data),

  getStaffWorkQueue: (params: { userId: string; accountId?: string }) =>
    api.get('/executions/steps/work-queue', { params }),
}

export const socialPostReviewAPI = {
  get: (executionId: string) => api.get(`/executions/${executionId}/social-post`),

  save: (executionId: string, body: { text: string; link?: string | null; media?: unknown[] }) =>
    api.put(`/executions/${executionId}/social-post`, body),

  uploadMedia: (executionId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post(`/executions/${executionId}/social-post/media`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  submitReview: (executionId: string, note?: string) =>
    api.post(`/executions/${executionId}/social-post/submit-review`, note ? { note } : {}),

  approve: (executionId: string, note?: string) =>
    api.post(`/executions/${executionId}/social-post/approve`, note ? { note } : {}),

  sendBack: (executionId: string, reason: string) =>
    api.post(`/executions/${executionId}/social-post/send-back`, { reason }),

  reject: (executionId: string, reason: string) =>
    api.post(`/executions/${executionId}/social-post/reject`, { reason }),
}

export const orgStaffAPI = (orgId: string) => ({
  list: (params?: Record<string, string | number>) =>
    api.get(`/org/${orgId}/staff`, { params }),
  delete: (userId: string) =>
    api.delete(`/org/${orgId}/staff/${userId}`),
})

export const accountStaffAPI = (accountId: string) => ({
  list: (params?: Record<string, string | number>) =>
    api.get(`/accounts/${accountId}/staff`, { params }),
  delete: (userId: string, data?: { confirmDisableAgents?: boolean }) =>
    api.delete(`/accounts/${accountId}/staff/${userId}`, { data }),
  enablePortalAccess: (userId: string, role: 'account_member' | 'account_admin') =>
    api.post(`/accounts/${accountId}/staff/${userId}/portal-access`, { role }),
  disablePortalAccess: (userId: string) =>
    api.delete(`/accounts/${accountId}/staff/${userId}/portal-access`),
})

// VP-1436: agency-global account plan list. Mounted at /org/:orgId/plans on the
// backend — a cross-account, paginated view; the account-scoped accountPlansAPI
// above is still the only path for reads of a single plan and all writes.
export const orgAccountPlansAPI = (orgId: string) => ({
  getAll: (params?: Record<string, string | number>) =>
    api.get(`/org/${orgId}/plans`, { params }),
})

// VP-1521: agency-wide cross-account calendar. Mounted at /org/:orgId/calendar on
// the backend — aggregates execution slots across multiple accounts with optional
// filtering by accountIds, agentTypes, statuses, and date ranges.
export const orgCalendarAPI = (orgId: string) => ({
  getAll: (params?: any) =>
    api.get(`/org/${orgId}/calendar`, { params }),
})

export const orgAccountsAPI = (orgId: string) => ({
  getAll: (params?: Record<string, string | number>) =>
    api.get(orgId ? `/org/${orgId}/accounts` : '/accounts', { params }),

  getById: (id: string) =>
    api.get(`/org/${orgId}/accounts/${id}`),

  create: (data: unknown) =>
    api.post(`/org/${orgId}/accounts`, data),

  update: (id: string, data: unknown) =>
    api.put(`/org/${orgId}/accounts/${id}`, data),

  delete: (id: string) =>
    api.delete(`/org/${orgId}/accounts/${id}`),
})

export const accountsAPI = {
  getById: (id: string) => api.get(`/accounts/${id}`),
  update: (id: string, data: unknown) => api.put(`/accounts/${id}`, data),
  delete: (id: string) => api.delete(`/accounts/${id}`),
  updatePainPoints: (id: string, painPoints: string[]) =>
    api.put(`/accounts/${id}/branding/pain-points`, { painPoints }),
  updateTargetKeywords: (id: string, targetKeywords: string[]) =>
    api.put(`/accounts/${id}/branding/target-keywords`, { targetKeywords }),
  updateTargetAudience: (id: string, targetAudience: string[]) =>
    api.put(`/accounts/${id}/branding/audience`, { targetAudience }),
  updateCTAs: (id: string, ctaList: { id: string; title: string; link: string }[]) =>
    api.put(`/accounts/${id}/branding/ctas`, { ctaList }),
  getBranding: (id: string) => api.get(`/accounts/${id}/branding`),
  updateBranding: (id: string, data: {
    industry?: string
    companyDescription?: string
    usp?: string
    ctaList: { id: string; title: string; link: string }[]
  }) => api.put(`/accounts/${id}/branding`, data),
  updateBusinessProfile: (id: string, data: {
    company?: string
    phone?: string
    website?: string
    address?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
    timezone?: string
  }) => api.patch(`/accounts/${id}/business-profile`, data),
  updateTerminology: (id: string, data: {
    verticalId?: string | null
    contactLabel?: string | null
    contactsLabel?: string | null
    dealLabel?: string | null
    dealsLabel?: string | null
    pipelineLabel?: string | null
    invoiceLabel?: string | null
    invoicesLabel?: string | null
    notesLabel?: string | null
  }) => api.patch(`/accounts/${id}/terminology`, data),

  uploadLogo: async (id: string, file: File): Promise<{ logoUrl: string }> => {
    const session = await getSession()
    const formData = new FormData()
    formData.append('logo', file)
    const res = await fetch(`${API_BASE_URL}/accounts/${id}/logo`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session?.accessToken}` },
      body: formData,
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to upload logo')
    return data as { logoUrl: string }
  },
}

export const verticalsAPI = {
  list: () => api.get('/verticals'),
}

export const accountICPsAPI = {
  getAll: (accountId: string, params?: { type?: string; isActive?: string; search?: string }) =>
    api.get(`/accounts/${accountId}/icps`, { params }),

  getById: (accountId: string, icpId: string) =>
    api.get(`/accounts/${accountId}/icps/${icpId}`),

  create: (accountId: string, data: any) =>
    api.post(`/accounts/${accountId}/icps`, data),

  update: (accountId: string, icpId: string, data: any) =>
    api.put(`/accounts/${accountId}/icps/${icpId}`, data),

  delete: (accountId: string, icpId: string) =>
    api.delete(`/accounts/${accountId}/icps/${icpId}`),
}

export const topicResearchAPI = {
  getIcpKeywords: (accountId: string) =>
    api.get(`/accounts/${accountId}/topic-research/icp-keywords`),

  getExecutionTopics: (executionId: string) =>
    api.get(`/executions/${executionId}/topic-research/topics`),

  setTopicStar: (executionId: string, topicId: string, starred: boolean) =>
    api.patch(`/executions/${executionId}/topic-research/topics/${topicId}/star`, { starred }),

  listAccountTopics: (
    accountId: string,
    params?: { status?: 'open' | 'superseded'; icpId?: string; forAgentId?: string },
  ) => api.get(`/accounts/${accountId}/content-topics`, { params }),
  listAccountBlogs: (accountId: string, params?: { forAgentId?: string }) =>
    api.get(`/accounts/${accountId}/blogs`, { params }),
}

export const newsItemsAPI = {
  list: (executionId: string, filter: string = 'all', page: number = 1, pageSize: number = 20) =>
    api.get(`/news-items`, { params: { executionId, filter, page, pageSize } }),

  star: (itemId: string) =>
    api.put(`/news-items/${itemId}/star`),

  unstar: (itemId: string) =>
    api.delete(`/news-items/${itemId}/star`),
}

export const accountOnboardingAPI = {
  getStatus: (accountId: string) =>
    api.get(`/accounts/${accountId}/onboarding-status`),

  updateStep: (accountId: string, step: string, completed: boolean) =>
    api.patch(`/accounts/${accountId}/onboarding`, { step, completed }),

  markComplete: (accountId: string) =>
    api.post(`/accounts/${accountId}/onboarding/complete`),
}

export const accountKnowledgeBaseAPI = {
  getAll: (
    accountId: string,
    params?: {
      search?: string
      category?: string
      department?: string
      sourceType?: string
      status?: string
      sortBy?: string
      sortDir?: string
      page?: number
      limit?: number
    }
  ) => api.get(`/accounts/${accountId}/kb`, { params }),

  create: (
    accountId: string,
    data: {
      name: string
      description?: string
      sourceType: string
      sourceUrl?: string
      category: string
      department?: string[]
      tags?: string[]
      autoTagging?: boolean
      convertToFaq?: boolean
      crawlEntireSite?: boolean
    }
  ) => api.post(`/accounts/${accountId}/kb`, data),

  delete: (accountId: string, kbId: string) =>
    api.delete(`/accounts/${accountId}/kb/${kbId}`),

  toggleStatus: (accountId: string, kbId: string, status: 'active' | 'inactive') =>
    api.patch(`/accounts/${accountId}/kb/${kbId}/status`, { status }),

  refresh: (accountId: string, kbId: string) =>
    api.post(`/accounts/${accountId}/kb/${kbId}/refresh`),

  uploadDocument: async (accountId: string, kbId: string, file: File) => {
    const session = await getSession()
    const formData = new FormData()
    formData.append('file', file)
    const res = await fetch(`${API_BASE_URL}/accounts/${accountId}/kb/${kbId}/documents`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session?.accessToken}` },
      body: formData,
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to upload document')
    return data
  },

  getDocuments: (accountId: string, kbId: string) =>
    api.get(`/accounts/${accountId}/kb/${kbId}/documents`),
}

export const accountAgentsAPI = {
  getAgents: (accountId: string, params?: { type?: string; status?: boolean }) =>
    api.get(`/accounts/${accountId}/agents`, { params }),

  create: (accountId: string, data: {
    name: string
    description?: string
    type: string
    agentTypeId?: string
    frequency: string
    scheduleDayOfWeek?: string
    scheduleDayOfMonth?: string
    scheduleTime?: string
    configuration?: any
    isActive?: boolean
    provider?: string
    workflowRef?: string
    departmentId?: string | null
    ownerUserId?: string | null
  }) => api.post(`/accounts/${accountId}/agents`, data),

  updateAgentSettings: (accountId: string, agentId: string, data: {
    configuration?: any;
    isActive?: boolean;
    frequency?: string;
    scheduleDayOfWeek?: string;
    scheduleDayOfMonth?: string;
    scheduleTime?: string;
    provider?: string;
    workflowRef?: string;
    departmentId?: string | null;
  }) => api.put(`/accounts/${accountId}/agents/${agentId}`, data),
}

export const newslettersAPI = {
  generate: (accountId: string, agentId: string, agentExecutionId: string) =>
    api.post(`/accounts/${accountId}/agents/${agentId}/newsletters/generate`, { agentExecutionId }),

  getLatest: (accountId: string, agentId: string, executionId?: string | null) =>
    api.get(`/accounts/${accountId}/agents/${agentId}/newsletters/latest`, {
      params: executionId ? { executionId } : undefined
    }),

  getCandidates: (accountId: string, agentId: string, executionId?: string | null) =>
    api.get(`/accounts/${accountId}/agents/${agentId}/newsletters/candidates`, {
      params: executionId ? { executionId } : undefined
    }),

  update: (accountId: string, agentId: string, newsletterId: string, data: {
    title?: string
    brandBlurb?: string
    bodyContent?: any
    status?: 'draft' | 'reviewed' | 'approved' | 'published'
  }) => api.patch(`/accounts/${accountId}/agents/${agentId}/newsletters/${newsletterId}`, data),

  deselectFeedItem: (accountId: string, agentId: string, newsletterId: string, feedItemId: string) =>
    api.patch(`/accounts/${accountId}/agents/${agentId}/newsletters/${newsletterId}/feed-items`, {
      action: 'deselect',
      feedItemId
    }),

  swapFeedItem: (accountId: string, agentId: string, newsletterId: string, feedItemId: string, replacementFeedItemId: string) =>
    api.patch(`/accounts/${accountId}/agents/${agentId}/newsletters/${newsletterId}/feed-items`, {
      action: 'swap',
      feedItemId,
      replacementFeedItemId
    }),

  recompose: (accountId: string, agentId: string, newsletterId: string) =>
    api.post(`/accounts/${accountId}/agents/${agentId}/newsletters/${newsletterId}/recompose`),

  compose: (accountId: string, agentId: string, agentExecutionId: string, articles: Array<{
    inputUrl: string
    articleUrl: string
    normalizedUrl?: string
    headline: string
    summary?: string | null
    imageUrl?: string | null
    category?: string | null
  }>, headerImageUrl?: string | null) =>
    api.post(`/accounts/${accountId}/agents/${agentId}/newsletters/compose`, { agentExecutionId, articles, headerImageUrl }),

  finalizeReview: (executionId: string, data: {
    title?: string
    brandBlurb?: string
    bodyContent?: any
  }) => api.post(`/executions/${executionId}/newsletter/finalize-review`, data),

  approve: (executionId: string, notes?: string) =>
    api.post(`/executions/${executionId}/newsletter/approve`, { notes }),

  reject: (executionId: string, notes?: string) =>
    api.post(`/executions/${executionId}/newsletter/reject`, { notes }),

  publish: (executionId: string, data?: { html?: string }) =>
    api.post(`/executions/${executionId}/newsletter/publish`, data || {}),
}

export const pageAuditsAPI = {
  start: (accountId: string, agentId: string, data: {
    data: {
      pages: Array<{
        url: string
        targetKeyword: string
        catalogItemId?: string
      }>
    }
  }) => api.post(`/accounts/${accountId}/agents/${agentId}/page-audits/start`, data),

  getAuditReportStatus: (executionId: string) => api.get(`/executions/${executionId}/seo-aeo-audit/audit-report`),

  getRecommendationsReview: (executionId: string) => api.get(`/executions/${executionId}/seo-aeo-audit/recommendations-review`),

  submitRecommendationsReview: (executionId: string, data: { recommendations: Record<string, any> }) =>
    api.post(`/executions/${executionId}/seo-aeo-audit/recommendations-review`, data),

  getApprovePushReview: (executionId: string) => api.get(`/executions/${executionId}/seo-aeo-audit/approve-push`),

  submitApprovePush: (executionId: string) => api.post(`/executions/${executionId}/seo-aeo-audit/approve-push`, {}),
}

export const integrationsAPI = {
  // Get all integration apps
  // `scope` selects which side's apps to list — 'account' for anything rendering
  // an account's integrations (agency staff viewing an account included), 'agency'
  // (the default) for the agency's own. Account users are pinned to 'account'
  // server-side regardless of what is passed.
  getApps: (params?: { scope?: 'account' | 'agency'; category?: string }) =>
    api.get('/integrations/apps', { params }),

  // Get single integration app
  getApp: (appId: string) =>
    api.get(`/integrations/apps/${appId}`),

  // Get all configurations for an account
  getAccountConfigurations: (accountId: string) =>
    api.get(`/integrations/configurations/${accountId}`),

  // Get specific app configuration for an account
  getConfiguration: (accountId: string, appId: string) =>
    api.get(`/integrations/configurations/${accountId}/${appId}`),

  // Save app configuration for an account
  saveConfiguration: (accountId: string, appId: string, configurations: Record<string, any>) =>
    api.post(`/integrations/configurations/${accountId}/${appId}`, { configurations }),

  // Delete app configuration
  deleteConfiguration: (accountId: string, appId: string) =>
    api.delete(`/integrations/configurations/${accountId}/${appId}`),

  // Update integration app configurations (admin only)
  updateAppConfigurations: (appId: string, configurations: Record<string, any>) =>
    api.put(`/integrations/apps/${appId}/configurations`, { configurations }),

  // Set default page for an account's integration
  setDefaultPage: (accountId: string, appId: string, defaultPageId: string) =>
    api.patch(`/integrations/configurations/${accountId}/${appId}/default-page`, { defaultPageId }),
}

export const fetchAgencyIntegrations = (orgId: string) =>
  api.get(`/org/${orgId}/integrations`)

export const oauthAPI = {
  // Initiate OAuth flow - returns authorization URL
  initiateOAuth: (provider: string, accountId: string, appId: string) =>
    api.get(`/oauth/${provider}/initiate`, { params: { accountId, appId } }),
}

export const socialAPI = {
  getAccountConnections: (accountId: string) =>
    api.get(`/social/connections/account/${accountId}`),

  connect: (accountId: string, appId: string) =>
    api.post(`/social/accounts/${accountId}/apps/${appId}/connect`),

  getLoginTargets: (accountId: string, appId: string) =>
    api.get(`/social/accounts/${accountId}/apps/${appId}/targets`),

  syncLoginTargets: (accountId: string, appId: string) =>
    api.post(`/social/accounts/${accountId}/apps/${appId}/targets/sync`),

  saveLoginTargets: (
    accountId: string,
    appId: string,
    data: { selectedIds: string[]; defaultId: string | null }
  ) => api.put(`/social/accounts/${accountId}/apps/${appId}/targets`, data),

  getSummary: (params: {
    accountId?: string
    platform?: string
    groupBy?: 'connection' | 'platform' | 'account'
    range?: string
    from?: string
    to?: string
  }) => api.get('/social/metrics/summary', { params }),

  getTimeSeries: (connectionId: string, params: { range?: string; from?: string; to?: string }) =>
    api.get(`/social/metrics/${connectionId}`, { params }),

  getPostPerformance: (params: {
    accountId?: string
    connectionId?: string
    platform?: string
    sort?: 'engagement' | 'reach' | 'impressions' | 'published_at'
    range?: string
    from?: string
    to?: string
    limit?: number
    offset?: number
  }) => api.get('/social/metrics/posts', { params }),
}

export const wordpressAPI = {
  // Publish blog post to WordPress (as draft)
  publishPost: (accountId: string, data: {
    title: string
    content: string
    excerpt?: string
    featuredImageUrl?: string
    categories?: number[]
    tags?: number[]
    meta?: {
      description?: string
      keywords?: string
    }
  }) => api.post(`/wordpress/publish/${accountId}`, data),

  // Test WordPress connection
  testConnection: (accountId: string) =>
    api.post(`/wordpress/test/${accountId}`),
}

export const adminAPI = {
  getAgencies: (params?: Record<string, string | number>) =>
    api.get('/admin/organizations', { params }),

  getAgency: (id: string) =>
    api.get(`/admin/organizations/${id}`),

  createAgency: (data: {
    name: string
    slug: string
    ownerFirstName: string
    ownerLastName: string
    ownerEmail: string
  }) => api.post('/admin/organizations', data),

  updateAgency: (id: string, data: {
    name?: string
    slug?: string
    email?: string | null
    website?: string | null
    ownerFirstName?: string
    ownerLastName?: string
    ownerEmail?: string
  }) => api.patch(`/admin/organizations/${id}`, data),
}

export const organizationsAPI = {
  get: () => api.get('/organizations'),
  update: (data: {
    name?: string
    address?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
    timezone?: string
    phoneNumber?: string
    website?: string
    logo?: string
    email?: string
  }) => api.put('/organizations', data),

  uploadLogo: async (file: File): Promise<{ logo: string }> => {
    const session = await getSession()
    const formData = new FormData()
    formData.append('logo', file)
    const res = await fetch(`${API_BASE_URL}/organizations/logo`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session?.accessToken}` },
      body: formData,
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to upload logo')
    return data as { logo: string }
  },
}

export const usersAPI = {
  getProfile: (userId: string) =>
    api.get(`/users/${userId}`),

  updateProfile: (userId: string, data: {
    firstName: string
    lastName: string
    phone: string
    timezone: string
  }) => api.patch(`/users/${userId}/profile`, data),

  changePassword: (userId: string, data: { currentPassword: string; newPassword: string }) =>
    api.patch(`/users/${userId}/update-password`, data),
}

export const capabilityTypesAPI = {
  getAll: () => api.get<{ data: CapabilityTypeOption[] }>('/capability-types'),
  getAllAdmin: () => api.get('/admin/capability-types'),
}

export const masterAgentTypesAPI = {
  getAll:   () => api.get('/master-agent-types'),
  getSteps: (id: string) => api.get(`/master-agent-types/${id}/steps`),
  getAllAdmin: () => api.get('/admin/agent-types'),
  updateAdmin: (id: string, patch: { capabilityTypeKey: string }) => api.patch(`/admin/agent-types/${id}`, patch),
  getByIdAdmin: (id: string) => api.get(`/admin/agent-types/${id}`),
  getEvalProfileAdmin: (id: string) => api.get(`/admin/agent-types/${id}/eval-profile`),
  updateEvalCheckAdmin: (agentTypeId: string, checkId: string, patch: Partial<{
    threshold: number | null
    severity: 'warn' | 'block'
    parameters: Record<string, unknown>
    isActive: boolean
  }>) => api.patch(`/admin/agent-types/${agentTypeId}/eval-profile/${checkId}`, patch),
  updateStep: (agentTypeId: string, stepId: string, patch: Partial<{
    stepName: string
    stepType: 'human' | 'workflow' | 'condition'
    role: string | null
    workflowId: string | null
    leadTime: string | null
    description: string | null
    isRequired: boolean
    billingUnits: number
    iconUrl: string | null
    conditionConfig: Record<string, unknown> | null
  }>) => api.patch(`/admin/agent-types/${agentTypeId}/steps/${stepId}`, patch),
}

type WorkflowPayload = {
  name: string
  description?: string
  providerId: string
  providerWorkflowId: string
  capabilityRef?: string
  status?: 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED'
  inputs?: string | null
  outputs?: string | null
  invocationMode?: 'SYNC' | 'ASYNC'
  timeoutSeconds?: number
  httpMethod?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE'
  retryLimit?: number
  retryDelaySeconds?: number
  callbackPath?: string | null
  headers?: Record<string, string> | null
  pollIntervalSeconds?: number | null
  inputMapping?: Record<string, { source: 'constant' | 'path' | 'none' | 'runtime'; value?: any; path?: string; fieldKey?: string }> | null
}

export const workflowsAPI = {
  getAll: (params?: { search?: string; status?: string; providerId?: string; sortBy?: string; sortDir?: string; page?: number; limit?: number }) =>
    api.get('/admin/workflows', { params }),
  getById: (id: string) =>
    api.get(`/admin/workflows/${id}`),
  create: (data: WorkflowPayload) =>
    api.post('/admin/workflows', data),
  update: (id: string, data: Partial<WorkflowPayload>) =>
    api.patch(`/admin/workflows/${id}`, data),
  remove: (id: string) =>
    api.delete(`/admin/workflows/${id}`),
  getSourceCatalog: (workflowId?: string) =>
    api.get('/admin/workflows/source-catalog', { params: { workflowId } }),
  test: (id: string, inputData: Record<string, any>) =>
    api.post(`/admin/workflows/${id}/test`, { inputData }),
}

export const fieldKeysAPI = {
  list: (params?: { domainObject?: string; status?: string; isFilterable?: boolean; isReportable?: boolean; setBy?: 'workflow' | 'system' | 'user' }) =>
    api.get('/admin/field-keys', { params }),
  domainObjects: () => api.get('/admin/field-keys/domain-objects'),
}

export const workflowProvidersAPI = {
  getAll: () => api.get('/admin/workflow-providers'),
}

export const usageRatesAPI = {
  get: () => api.get('/admin/usage-rates'),
  update: (eventType: 'agent_run' | 'va_task_hours', rateCents: number) =>
    api.put(`/admin/usage-rates/${eventType}`, { rateCents }),
}

export const agentStepsAPI = (agentId: string) => ({
  getAll: () => api.get(`/agents/${agentId}/steps`),
  save:   (steps: any[]) => api.put(`/agents/${agentId}/steps`, { steps }),
})

export const departmentsAPI = {
  getAll: (accountId?: string) =>
    api.get<Department[]>('/departments', { params: accountId ? { accountId } : undefined }),
  create: (data: { name: string; accountId: string; description?: string; parentId?: string | null }) =>
    api.post('/departments', data),
  update: (id: string, data: { name?: string; description?: string; parentId?: string | null }) =>
    api.put(`/departments/${id}`, data),
  delete: (id: string, force?: boolean) =>
    api.delete(`/departments/${id}`, force ? { params: { force: 'true' } } : undefined),
  getMembers: (id: string) =>
    api.get(`/departments/${id}/members`),
  addMember: (id: string, userId: string) =>
    api.post(`/departments/${id}/members`, { userId }),
  removeMember: (id: string, userId: string) =>
    api.delete(`/departments/${id}/members/${userId}`),
  getAgents: (id: string) =>
    api.get(`/departments/${id}/agents`),
  getCatalogs: (id: string) =>
    api.get(`/departments/${id}/catalogs`),
}

export const reportsAPI = {
  getSummary: (params?: ReportQueryParams) => api.get('/reports/summary', { params }),
  getExecutions: (params?: ReportQueryParams) => api.get('/reports/executions', { params }),
  getSteps: (params?: ReportQueryParams) => api.get('/reports/steps', { params }),
  getUsage: (params?: ReportQueryParams) => api.get('/reports/usage', { params }),
  getNav: (params: { accountId: string }) => api.get<ReportNavResponse>('/reports/nav', { params }),
  getPastExecutions: (params: { accountId: string; reportType?: string; preset?: ReportPreset; from?: string; to?: string; page?: number; pageSize?: number }) =>
    api.get<ReportPastResponse>('/reports/past', { params }),
  getScheduled: (params: { accountId: string; reportType?: string }) =>
    api.get<ReportScheduledResponse>('/reports/scheduled', { params }),
  createScheduledReport: (params: { accountId: string } & CreateScheduledReportInput) =>
    api.post<CreateScheduledReportResponse>('/reports/scheduled', {
      name: params.name,
      reportTypeKey: params.reportTypeKey,
      frequency: params.frequency,
      day: params.day,
      time: params.time,
      format: params.format,
      deliveryRecipients: params.deliveryRecipients,
      message: params.message,
      dateRangePreset: params.dateRangePreset,
    }, { params: { accountId: params.accountId } }),
  previewScheduledReport: (reportId: string, params: { accountId: string; subjectId: string }) =>
    api.post<PreviewScheduledReportResponse>(`/reports/scheduled/${reportId}/preview`, { subjectId: params.subjectId }, { params: { accountId: params.accountId } }),
  toggleScheduledStatus: (reportId: string, params: { accountId: string; isActive: boolean }) =>
    api.patch<{ success: boolean; data: ScheduledReportDTO }>(`/reports/scheduled/${reportId}/status`, { isActive: params.isActive }, { params: { accountId: params.accountId } }),
  updateScheduledSchedule: (reportId: string, params: { accountId: string } & ScheduleDetailInput) =>
    api.patch<{ success: boolean; data: ScheduledReportDTO }>(`/reports/scheduled/${reportId}`, {
      frequency: params.frequency,
      day: params.day,
      time: params.time,
      format: params.format,
      deliveryRecipients: params.deliveryRecipients,
    }, { params: { accountId: params.accountId } }),
  runScheduledReport: (reportId: string, params: { accountId: string }) =>
    api.post<TriggerReportExecutionResponse>(`/reports/scheduled/${reportId}/run`, undefined, { params: { accountId: params.accountId } }),
  deleteScheduledReport: (reportId: string, params: { accountId: string }) =>
    api.delete<{ success: boolean }>(`/reports/scheduled/${reportId}`, { params: { accountId: params.accountId } }),
  getDetail: (params: {
    accountId: string
    reportTypeKey: string
    subjectId: string
    preset?: ReportPreset
    from?: string
    to?: string
    chartGroupBy?: ChartGroupBy
    page?: number
    pageSize?: number
    sortBy?: string
    sortDir?: 'asc' | 'desc'
  }) => api.get<ReportDetailResponse>('/reports/detail', { params }),
  getNewsletterExecutions: (params: { accountId?: string; startDate: string; endDate: string }) =>
    api.get<NewsletterExecutionsListResponse>('/reports/newsletter/executions', { params }),
  getNewsletterExecutionDetail: (executionId: string, params?: { accountId?: string }) =>
    api.get<NewsletterExecutionDetailResponse>(`/reports/newsletter/executions/${executionId}`, { params }),
}

export const accountCatalogAPI = {
  getAll: (
    accountId: string,
    params?: {
      search?: string
      type?: string
      isActive?: string
      sortBy?: string
      sortDir?: string
      page?: number
      limit?: number
    }
  ) => api.get(`/accounts/${accountId}/catalog`, { params }),
  create: (accountId: string, data: {
    name: string
    type: 'PRODUCT' | 'SERVICE' | 'INVESTMENT'
    shortDescription: string
    keyDifferentiator: string
    priceRangeOrTier?: string | null
    pageLink?: string | null
    targetIcpId?: string | null
    priority: number
    isActive?: boolean
    keywords?: string[]
  }) => api.post(`/accounts/${accountId}/catalog`, data),
  update: (accountId: string, itemId: string, data: {
    name?: string
    type?: 'PRODUCT' | 'SERVICE' | 'INVESTMENT'
    shortDescription?: string
    keyDifferentiator?: string
    priceRangeOrTier?: string | null
    pageLink?: string | null
    targetIcpId?: string | null
    departmentId?: string | null
    priority?: number
    isActive?: boolean
    keywords?: string[]
  }) => api.put(`/accounts/${accountId}/catalog/${itemId}`, data),
  remove: (accountId: string, itemId: string) => api.delete(`/accounts/${accountId}/catalog/${itemId}`),
  // 6 minutes: longer than N8nAdapter's hardcoded 5-minute (300_000ms) call ceiling,
  // since this route can legitimately take that long end-to-end.
  loadFromWebsite: (accountId: string, filterPaths: string[]) =>
    api.post(
      `/accounts/${accountId}/catalog/load-from-website`,
      { filterPaths },
      { timeout: 360000 }
    ),
}

// VP-1696: account-scoped tag management.
export const accountTagsAPI = (accountId: string) => ({
  list: (params?: { status?: 'active' | 'archived' | 'all'; search?: string }) =>
    api.get<{ success: boolean; data: import('@/types/tag').AccountTag[] }>(
      `/accounts/${accountId}/tags`,
      { params },
    ),

  create: (name: string) =>
    api.post<{ success: boolean; data: import('@/types/tag').AccountTag; archived?: boolean }>(
      `/accounts/${accountId}/tags`,
      { name },
    ),

  rename: (tagId: string, name: string) =>
    api.put<{ success: boolean; data: import('@/types/tag').AccountTag }>(
      `/accounts/${accountId}/tags/${tagId}`,
      { name },
    ),

  archive: (tagId: string) =>
    api.patch<{ success: boolean; data: import('@/types/tag').AccountTag }>(
      `/accounts/${accountId}/tags/${tagId}/archive`,
    ),

  restore: (tagId: string) =>
    api.patch<{ success: boolean; data: import('@/types/tag').AccountTag }>(
      `/accounts/${accountId}/tags/${tagId}/restore`,
    ),

  setLinks: (domainObject: string, recordId: string, tagIds: string[]) =>
    api.put<{ success: boolean; data: import('@/types/tag').TagOption[] }>(
      `/accounts/${accountId}/tags/links`,
      { domainObject, recordId, tagIds },
    ),

  getLinks: (domainObject: string, params: { recordId?: string; recordIds?: string }) =>
    api.get<{ success: boolean; data: Record<string, import('@/types/tag').TagOption[]> | import('@/types/tag').TagOption[] }>(
      `/accounts/${accountId}/tags/links`,
      { params: { domainObject, ...params } },
    ),

  getTargets: (domainObject: string, tagIds: string[], mode: import('@/types/tag').TagMatchMode = 'any') =>
    api.get<{ success: boolean; data: string[] }>(
      `/accounts/${accountId}/tags/links/targets`,
      { params: { domainObject, tagIds: tagIds.join(','), mode } },
    ),
})

export default api
